/** Warms the main screens after sign-in and on each return to the app: their
 *  code first, then the reads they draw (Home, then the More destinations and
 *  Profile), so a tab opens already filled. The first run waits for the
 *  account's data to restore and for idle time. */

import { useEffect, useRef } from 'react';
import { DataService } from '../services/DataService';
import { currentBackgroundReadScope, readInBackground } from '../services/BackgroundReadCache';
import { backgroundWarmAllowed, MAIN_REWARM_AFTER_MS, runWarmTasks, whenIdle, type WarmTask } from '../services/mainScreenWarm';
import {
  draftCisReturnsRead,
  expiringSiteCardsRead,
  expiringSubcontractorCoverRead,
  homeReadWorkspaces,
  localDayKey,
  londonDayKey,
  lolerPlantAssetsRead,
  submittedExpenseClaimsRead,
  unpaidSalesInvoicesRead,
  type HomeRead,
} from '../features/dashboard/homeComingUpReads';
import { moreReadWorkspaces, moreWarmTasks, type MoreWarmAuth, type MoreWarmNavigation } from './moreScreenWarm';

// Importing primes the module cache, so a lazy view renders without waiting on
// its chunk. These are the tabs people reach first, the project they open and Calendar.
const MAIN_SCREEN_CHUNKS: WarmTask[] = [
  () => import('../features/projects/ProjectListView'),
  () => import('../features/projects/ProjectDetail'),
  () => import('../features/projects/detail/ProjectOverviewTab'),
  () => import('../features/messages/MessagesView'),
  () => import('../features/notifications/NotificationsView'),
  () => import('../platform/navigation/MoreView'),
  () => import('../features/profile/UserProfile'),
  () => import('../features/calendar/CalendarPage'),
];
const OPERATIONS_HOME_CHUNK: WarmTask = () => import('../features/dashboard/QSDashboard');

type WarmAuth = Parameters<typeof homeReadWorkspaces>[0] & MoreWarmAuth;

/** A read that never answers must not hold back every later warm. */
const SUPERSEDED_WARM_WAIT_MS = 20_000;

export function useMainScreenWarm(auth: WarmAuth, navigation: MoreWarmNavigation): void {
  const userId = auth.user?.id;
  const { siteCards, cis, expenses } = homeReadWorkspaces(auth);
  const more = moreReadWorkspaces(auth, navigation);
  const previousRun = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!userId) return;
    let current = true;
    let running = false;
    let lastWarm = 0;
    let cancelIdle = () => {};
    const isCurrent = () => current && backgroundWarmAllowed();

    const homeReads = (): WarmTask[] => {
      const tasks: WarmTask[] = [];
      const add = <T,>(workspaceId: string | undefined, read: (workspaceId: string, day: string) => HomeRead<T>, day: string) => {
        const scope = currentBackgroundReadScope(workspaceId);
        if (!scope || !workspaceId || scope.actorId !== userId) return;
        const { name, load } = read(workspaceId, day);
        tasks.push(() => readInBackground(scope, name, load, { maxAgeMs: MAIN_REWARM_AFTER_MS }));
      };
      add(siteCards, expiringSiteCardsRead, localDayKey());
      add(siteCards, expiringSubcontractorCoverRead, localDayKey());
      add(siteCards, lolerPlantAssetsRead, localDayKey());
      add(cis, draftCisReturnsRead, londonDayKey());
      add(expenses, submittedExpenseClaimsRead, londonDayKey());
      add(more.salesInvoices, unpaidSalesInvoicesRead, londonDayKey());
      return tasks;
    };

    const warm = async () => {
      if (running || !isCurrent() || Date.now() - lastWarm < MAIN_REWARM_AFTER_MS) return;
      running = true;
      const prior = previousRun.current;
      let finished = () => {};
      previousRun.current = new Promise<void>(resolve => { finished = resolve; });
      try {
        // A superseded warm (grants or a workspace arrived) stops starting reads but
        // finishes those in flight; wait for it, so the two lanes are never doubled.
        await Promise.race([prior, new Promise<void>(resolve => { window.setTimeout(resolve, SUPERSEDED_WARM_WAIT_MS); })]);
        if (!isCurrent()) return;
        await runWarmTasks(siteCards ? [...MAIN_SCREEN_CHUNKS, OPERATIONS_HOME_CHUNK] : MAIN_SCREEN_CHUNKS, isCurrent);
        await runWarmTasks(homeReads(), isCurrent);
        await runWarmTasks(moreWarmTasks(userId, more), isCurrent);
        // A warm cut short by hiding the app is retried on the next return, not 5 min later.
        if (isCurrent()) lastWarm = Date.now();
      } finally {
        running = false;
        finished();
      }
    };
    const schedule = () => {
      cancelIdle();
      cancelIdle = whenIdle(() => { void warm(); });
    };
    void DataService.waitForInitialization().then(() => { if (current) schedule(); });
    const onVisible = () => { if (document.visibilityState === 'visible') schedule(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      current = false;
      cancelIdle();
      document.removeEventListener('visibilitychange', onVisible);
    };
  // `more` is rebuilt each render; its workspaces below are what change its tasks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, siteCards, cis, expenses, more.outreach, more.salesInvoices, more.purchases, more.payroll, more.sharedInbox, more.workspaceDirectory, more.calendar, more.profile]);
}
